import { chordSheetTooBig } from "@bandstand/core/ChordSheet";
import { observer } from "mobx-react-lite";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, IconButton, SmallHeading } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { Segmented } from "../ui/Segmented";
import { SheetView } from "../sheet/SheetView";
import { SaveDialog } from "./SaveDialog";

/** On a phone, the text and the preview take turns. */
const PHONE_PANES: ["text" | "preview", string][] = [
    ["text", "Text"],
    ["preview", "Preview"],
];

/** Editing a chord sheet as ChordPro text, with a preview. These are real changes, saved in one of three ways. */
export const EditScreen = observer(function EditScreen({ chordSheetId }: { chordSheetId: string }) {
    const { editor, library, display } = useStores();
    const [saving, setSaving] = useState(false);
    const [phonePane, setPhonePane] = useState<"text" | "preview">("text");
    const loaded = editor.chordSheetId === chordSheetId;
    const from = editor.from;
    const tooBig = chordSheetTooBig(editor.text);
    const saved = from.kind === "setlist" ? `From ${library.setlist(from.setlistId)?.name}` : "Changes are saved to the chord sheet";
    const subtitle = tooBig ? "This is too long for a chord sheet" : saved;

    useEffect(() => {
        void editor.load(chordSheetId);
    }, [editor, chordSheetId]);

    const type = (event: ChangeEvent<HTMLTextAreaElement>) => editor.setText(event.target.value);

    function cancel() {
        if (editor.changed && !confirm("Stop editing? Your changes won't be saved.")) return;

        editor.cancel();
    }

    return (
        <Screen>
            <Bar>
                <Button $kind="quiet" onClick={cancel}>
                    Cancel
                </Button>
                <Heading>
                    <Title>Editing {library.title(chordSheetId)}</Title>
                    <Subtitle $warning={tooBig}>{subtitle}</Subtitle>
                </Heading>
                <Button $kind="primary" disabled={!loaded || !editor.changed || tooBig} onClick={() => setSaving(true)}>
                    Save…
                </Button>
            </Bar>
            <Tools>
                <SmallHeading as="span">Key of the chord sheet</SmallHeading>
                <IconButton $bordered aria-label="Key down" onClick={() => editor.transpose(-1)}>
                    <Icon name="minus" />
                </IconButton>
                <Key>{editor.preview.key ?? "–"}</Key>
                <IconButton $bordered aria-label="Key up" onClick={() => editor.transpose(1)}>
                    <Icon name="plus" />
                </IconButton>
                <Note>Changes every chord in the file itself.</Note>
                <Button onClick={() => editor.switchTo("arrange")}>Arrange by touch</Button>
                <Phone>
                    <Segmented label="Show" options={PHONE_PANES} value={phonePane} onChange={setPhonePane} />
                </Phone>
            </Tools>
            <Panes>
                <Pane $hiddenOnPhone={phonePane !== "text"}>
                    <Text aria-label="ChordPro text" spellCheck={false} value={loaded ? editor.text : ""} onChange={type} />
                </Pane>
                <Pane aria-label="Preview" $hiddenOnPhone={phonePane !== "preview"}>
                    <Preview>
                        <SheetView sheet={editor.preview} lyricsOnly={false} textSize={Math.min(display.textSize, 19)} />
                    </Preview>
                </Pane>
            </Panes>
            {saving && <SaveDialog onClose={() => setSaving(false)} />}
        </Screen>
    );
});

const Screen = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const Bar = styled.header`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    padding-top: max(6px, env(safe-area-inset-top));
    border-bottom: 1px solid ${color.border};
`;

const Heading = styled.div`
    flex: 1;
    min-width: 0;
    text-align: center;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div<{ $warning: boolean }>`
    font-size: 13px;
    color: ${({ $warning }) => ($warning ? color.danger : color.textMuted)};
`;

const Tools = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid ${color.border};
    font-size: 14px;
    color: ${color.textSoft};
`;

const Key = styled.span`
    min-width: 32px;
    text-align: center;
    font-weight: 700;
    color: ${color.text};
`;

const Note = styled.span`
    font-size: 13px;
    color: ${color.textMuted};
`;

const Phone = styled.div`
    margin-left: auto;

    @media (min-width: 700px) {
        display: none;
    }
`;

const Panes = styled.div`
    flex: 1;
    min-height: 0;
    display: grid;

    @media (min-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Pane = styled.section<{ $hiddenOnPhone: boolean }>`
    min-height: 0;
    display: flex;
    flex-direction: column;

    @media (max-width: 699px) {
        display: ${({ $hiddenOnPhone }) => ($hiddenOnPhone ? "none" : "flex")};
    }
`;

const Text = styled.textarea`
    flex: 1;
    min-height: 0;
    padding: 16px 20px;
    border: 0;
    border-right: 1px solid ${color.border};
    background: ${color.background};
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 15px;
    line-height: 1.7;
    resize: none;
    outline: none;
`;

const Preview = styled.div`
    flex: 1;
    overflow: auto;
    padding: 16px 28px;
    background: ${color.panel};
`;
